import { Text, VStack } from '@astryxdesign/core';

interface ForecastChartProps {
  history: number[];
  point: number[];
  /** Lower/upper band, typically P10 / P90. Omitted when quantiles are off. */
  lower?: number[];
  upper?: number[];
  height?: number;
  /** Trailing history points to draw; keeps long contexts readable. */
  historyWindow?: number;
}

const PAD = { top: 12, right: 12, bottom: 22, left: 46 };

/**
 * Hand-rolled SVG chart: two polylines and a band. A charting library would be
 * ~50 kB for one view, and the geometry here is a dozen lines of maths.
 */
export function ForecastChart({
  history,
  point,
  lower,
  upper,
  height = 260,
  historyWindow = 120,
}: ForecastChartProps) {
  const width = 720;
  const hist = history.slice(-historyWindow);

  if (hist.length === 0 && point.length === 0) {
    return (
      <Text size="sm" color="secondary">
        Nothing to plot yet.
      </Text>
    );
  }

  const hasBand = Boolean(lower && upper && lower.length === point.length && upper.length === point.length);

  const all = [...hist, ...point, ...(hasBand ? [...lower!, ...upper!] : [])];
  let min = Math.min(...all);
  let max = Math.max(...all);
  if (min === max) {
    // A flat series would divide by zero; give it an arbitrary but sane band.
    min -= 1;
    max += 1;
  }
  const span = max - min;
  min -= span * 0.08;
  max += span * 0.08;

  // History occupies indices [0, hist.length-1], the forecast continues from the
  // last history point so the two lines visually join.
  const total = Math.max(1, hist.length + point.length - 1);
  const plotW = width - PAD.left - PAD.right;
  const plotH = height - PAD.top - PAD.bottom;

  const x = (i: number) => PAD.left + (i / total) * plotW;
  const y = (v: number) => PAD.top + (1 - (v - min) / (max - min)) * plotH;

  const histPts = hist.map((v, i) => `${x(i).toFixed(2)},${y(v).toFixed(2)}`).join(' ');

  const forecastOffset = Math.max(0, hist.length - 1);
  const joinValue = hist.length > 0 ? hist[hist.length - 1] : undefined;
  const forecastSeries = joinValue === undefined ? point : [joinValue, ...point];
  const forecastPts = forecastSeries
    .map((v, i) => `${x(forecastOffset + i).toFixed(2)},${y(v).toFixed(2)}`)
    .join(' ');

  let bandPath = '';
  if (hasBand) {
    const up = upper!.map((v, i) => `${x(hist.length + i).toFixed(2)},${y(v).toFixed(2)}`);
    const lo = lower!
      .map((v, i) => `${x(hist.length + i).toFixed(2)},${y(v).toFixed(2)}`)
      .reverse();
    bandPath = `M ${up.join(' L ')} L ${lo.join(' L ')} Z`;
  }

  const ticks = [min, min + (max - min) / 2, max];
  const divider = hist.length > 0 ? x(forecastOffset) : null;

  return (
    <VStack gap={1.5}>
      <div className="chart-wrap">
        <svg
          className="chart"
          viewBox={`0 0 ${width} ${height}`}
          preserveAspectRatio="xMidYMid meet"
          role="img"
          aria-label={`Forecast chart: ${hist.length} historical points followed by ${point.length} forecast points`}
        >
          {ticks.map((t) => (
            <g key={t}>
              <line
                x1={PAD.left}
                x2={width - PAD.right}
                y1={y(t)}
                y2={y(t)}
                stroke="var(--color-border)"
                strokeWidth={1}
              />
              <text
                x={PAD.left - 8}
                y={y(t) + 4}
                textAnchor="end"
                fontSize={11}
                fill="var(--color-text-secondary)"
              >
                {formatTick(t)}
              </text>
            </g>
          ))}

          {hasBand && <path d={bandPath} fill="var(--color-icon-blue)" opacity={0.18} />}

          {divider !== null && (
            <line
              x1={divider}
              x2={divider}
              y1={PAD.top}
              y2={height - PAD.bottom}
              stroke="var(--color-border-emphasized)"
              strokeWidth={1}
              strokeDasharray="4 4"
            />
          )}

          {hist.length > 1 && (
            <polyline points={histPts} fill="none" stroke="var(--color-text-primary)" strokeWidth={1.6} />
          )}
          {forecastSeries.length > 1 && (
            <polyline points={forecastPts} fill="none" stroke="var(--color-icon-blue)" strokeWidth={2.2} />
          )}

          <text
            x={width - PAD.right}
            y={height - 6}
            textAnchor="end"
            fontSize={11}
            fill="var(--color-text-secondary)"
          >
            +{point.length} steps
          </text>
          <text x={PAD.left} y={height - 6} fontSize={11} fill="var(--color-text-secondary)">
            {hist.length} observed
          </text>
        </svg>
      </div>

      <div className="legend">
        <span className="legend__item">
          <span className="legend__swatch" style={{ background: 'var(--color-text-primary)' }} />
          History
        </span>
        <span className="legend__item">
          <span className="legend__swatch" style={{ background: 'var(--color-icon-blue)' }} />
          Forecast (median)
        </span>
        {hasBand && (
          <span className="legend__item">
            <span
              className="legend__swatch"
              style={{ background: 'var(--color-icon-blue)', opacity: 0.4, height: 9 }}
            />
            P10–P90 band
          </span>
        )}
      </div>
    </VStack>
  );
}

function formatTick(v: number): string {
  const abs = Math.abs(v);
  if (abs >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `${(v / 1_000).toFixed(1)}k`;
  if (abs >= 10) return v.toFixed(0);
  return v.toFixed(2);
}
